import { reportWarning } from "@/lib/observability/report-error";
import { CURRENT_CACHE_VERSION } from "./hash-utils";

const DB_NAME = "vidseeds-ai-cache";
const DB_VERSION = 1;
const STORE_NAME = "responses";
const MAX_CACHE_SIZE_BYTES = 100 * 1024 * 1024; // 100 MB

interface CacheEntry {
  hash: string;
  response: string;
  createdAt: number;
  lastAccessedAt: number;
  ttl: number;
  taskType: string;
  sizeBytes: number;
  cacheVersion: string;
}

export const CACHE_TTLS = {
  meaning: 24 * 60 * 60 * 1000,
  voiceFingerprint: 48 * 60 * 60 * 1000,
  frameSummaries: 24 * 60 * 60 * 1000,
  intelligence: 6 * 60 * 60 * 1000,
  nicheDetection: 24 * 60 * 60 * 1000,
  thumbnailStyle: 24 * 60 * 60 * 1000,
} as const;

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    let request: IDBOpenDBRequest;
    try {
      request = indexedDB.open(DB_NAME, DB_VERSION);
    } catch (err) {
      reportWarning(
        err instanceof Error ? err : new Error("indexedDB.open failed"),
        {
          source: "lib.cache.client-ai-cache",
          feature: "cache",
          action: "openDB",
          handled: true,
        }
      );
      reject(err);
      return;
    }
    request.onupgradeneeded = () => {
      const db = request.result;
      // SENTRY FIX: NODE-2F9 — IndexedDB objectStoreNames crash
      // Date: 2026-04-13 | Agent: Claude Code
      // Root cause: request.result can be undefined in Safari private browsing
      // Fix: null guard before accessing db.objectStoreNames
      if (!db) return;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: "hash" });
        store.createIndex("lastAccessedAt", "lastAccessedAt");
        store.createIndex("cacheVersion", "cacheVersion");
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Read a cached AI response. Returns null on miss or expiry.
 * Uses a readonly transaction for the initial read, then a separate
 * readwrite transaction to update lastAccessedAt (best-effort, non-blocking).
 */
export async function getCachedResponse(hash: string): Promise<string | null> {
  let db: IDBDatabase | undefined;
  try {
    db = await openDB();

    // Read-only first
    const entry = await new Promise<CacheEntry | undefined>((resolve) => {
      const tx = db!.transaction(STORE_NAME, "readonly");
      const request = tx.objectStore(STORE_NAME).get(hash);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(undefined);
    });

    if (!entry) return null;
    if (Date.now() > entry.createdAt + entry.ttl) {
      // Expired — delete in background (best-effort)
      const tx = db.transaction(STORE_NAME, "readwrite");
      tx.objectStore(STORE_NAME).delete(hash);
      return null;
    }

    // Update lastAccessedAt for LRU (best-effort, non-blocking)
    try {
      const tx = db.transaction(STORE_NAME, "readwrite");
      entry.lastAccessedAt = Date.now();
      tx.objectStore(STORE_NAME).put(entry);
    } catch (error) {
      reportWarning(error, {
        source: "cache.client-ai-cache",
        feature: "client-ai-cache",
        action: "touchCacheEntry",
        extra: { hash },
      });
    }

    return entry.response;
  } catch (error) {
    reportWarning(error, {
      source: "cache.client-ai-cache",
      feature: "client-ai-cache",
      action: "getCachedResponse",
      extra: { hash },
    });
    return null;
  } finally {
    db?.close();
  }
}

/**
 * Store an AI response in the cache.
 */
export async function setCachedResponse(
  hash: string,
  response: string,
  taskType: string,
  ttl: number
): Promise<void> {
  let db: IDBDatabase | undefined;
  try {
    db = await openDB();
    const entry: CacheEntry = {
      hash,
      response,
      createdAt: Date.now(),
      lastAccessedAt: Date.now(),
      ttl,
      taskType,
      sizeBytes: new Blob([response]).size,
      cacheVersion: CURRENT_CACHE_VERSION,
    };

    await new Promise<void>((resolve, reject) => {
      const tx = db!.transaction(STORE_NAME, "readwrite");
      tx.objectStore(STORE_NAME).put(entry);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });

    // Trigger eviction check in background (non-blocking)
    evictIfOverBudget().catch((error) =>
      reportWarning(error, {
        source: "cache.client-ai-cache",
        feature: "client-ai-cache",
        action: "evictIfOverBudget",
        extra: { hash, taskType },
      })
    );
  } catch (error) {
    reportWarning(error, {
      source: "cache.client-ai-cache",
      feature: "client-ai-cache",
      action: "setCachedResponse",
      extra: { hash, taskType },
    });
  } finally {
    db?.close();
  }
}

/**
 * Evict entries by LRU (oldest lastAccessedAt first) if total exceeds budget.
 * All mutations happen inside the cursor callback chain to avoid
 * IndexedDB TransactionInactiveError.
 */
function evictIfOverBudget(): Promise<void> {
  return openDB().then(
    (db) =>
      new Promise<void>((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, "readwrite");
        const store = tx.objectStore(STORE_NAME);
        const index = store.index("lastAccessedAt");
        const entries: CacheEntry[] = [];

        // Collect all entries via cursor (sorted by lastAccessedAt ascending = LRU order)
        const cursorReq = index.openCursor();
        cursorReq.onsuccess = () => {
          const cursor = cursorReq.result;
          if (cursor) {
            entries.push(cursor.value as CacheEntry);
            cursor.continue();
          } else {
            // Cursor exhausted — check budget and delete within same transaction
            const totalSize = entries.reduce((s, e) => s + e.sizeBytes, 0);
            if (totalSize > MAX_CACHE_SIZE_BYTES) {
              let freed = 0;
              const toFree = totalSize - MAX_CACHE_SIZE_BYTES;
              for (const entry of entries) {
                if (freed >= toFree) break;
                store.delete(entry.hash);
                freed += entry.sizeBytes;
              }
            }
          }
        };
        tx.oncomplete = () => {
          db.close();
          resolve();
        };
        tx.onerror = () => {
          db.close();
          reject(tx.error);
        };
      })
  );
}

/**
 * Run on app boot: remove expired entries and orphaned cache versions.
 * Fire-and-forget — does not block app startup.
 */
export function cleanupCache(): void {
  openDB()
    .then((db) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      const cursorReq = store.openCursor();
      cursorReq.onsuccess = () => {
        const cursor = cursorReq.result;
        if (!cursor) return;
        const entry = cursor.value as CacheEntry;
        const isExpired = Date.now() > entry.createdAt + entry.ttl;
        const isOrphaned = entry.cacheVersion !== CURRENT_CACHE_VERSION;
        if (isExpired || isOrphaned) {
          cursor.delete();
        }
        cursor.continue();
      };
      tx.oncomplete = () => db.close();
      tx.onerror = () => db.close();
    })
    .catch((error) => {
      reportWarning(error, {
        source: "cache.client-ai-cache",
        feature: "client-ai-cache",
        action: "cleanupCache",
      });
    });
}
